'use client';

import React, { useEffect, useRef } from 'react';
import { type Dot, buildField } from './atmosphere/dotField';
import { type Frame, type Patch, makeSprite, paintDots } from './atmosphere/paintDots';

/**
 * The ground the page sits on, and the way it moves.
 *
 * Three things happen here, and only the last is decoration:
 *
 *  - Two large pools of light drift at their own rates as you scroll, slower
 *    than the content in front of them. That difference in rate is what makes a
 *    background read as behind the page rather than printed on it. They are
 *    neutral, and they are the same everywhere: a tinted pool drifting across
 *    the page would be a page that changes colour as you scroll it, which is
 *    what the tone system used to do here and why it is gone. The ground is one
 *    colour now — see the note above --m-ground-live in tailwind.css.
 *  - A field of fine dots in the two places on a page where nothing is ever
 *    written: the margins outside the content column, and the band of empty
 *    ground where one block meets the next. The margin field is simply always
 *    there; the seam band rides each boundary up the screen, drawing itself in
 *    from the left as the block arrives. Both roll on a wave as you scroll,
 *    each dot changing size and brightness with its height in it, which is what
 *    makes the whole thing read as a surface with a shape rather than as a
 *    pattern printed on the page. It is drawn on a canvas — see
 *    ./atmosphere/ — because a repeating CSS gradient can only ever be a grid,
 *    and a grid is exactly what a page's ground should not have.
 *  - A fine grain over the top, which stops the gradients banding on cheap
 *    panels and gives the whole thing a surface.
 *
 * Blocks mark themselves with `data-ground`, which is how the field knows where
 * one ends and the next begins. Anything without it is simply not a boundary,
 * so adding a section to the page cannot break this and forgetting to mark one
 * costs nothing but a missed band of dots.
 *
 * Everything is driven from one rAF: the scroll handler records a number, and
 * the frame writes custom properties and draws the field. Layout is read only
 * when the page changes size, never per frame — so a frame on which nothing
 * moved costs nothing at all.
 */

/** A boundary between two blocks: where its empty ground is, and how much. */
interface Seam {
  /** Middle of the gap, in document coordinates. */
  y: number;
  /** How much empty ground there is across it. */
  gap: number;
}

/** Below this a boundary has no room for a band, so it is not one. */
const MIN_GAP = 56;

/** Held back from each side of a measured gap, against a page still settling. */
const SAFETY = 18;

/**
 * Where a block's content actually starts and stops.
 *
 * Not the same as its padding. Most sections here put their breathing room on
 * the section, several put it on an inner wrapper, and one — the terms strip —
 * has none on the section at all. Reading `padding-top` would call those flush
 * and either drop a perfectly good seam or, worse, draw dots across the first
 * line of a paragraph.
 *
 * A Range over the block's contents answers it in one call: the union of every
 * line box, image and inline run inside, with the block's own padding left out
 * of it. That is worth saying plainly, because the obvious alternative is what
 * was here before — walk down the child that reaches furthest, a few levels,
 * skipping padding as you go. It is wrong, and wrong in the direction that
 * matters. The walk is greedy: it commits to one subtree at each level, so when
 * a section ends in a grid it finds the deepest thing inside whichever column
 * it picked and never sees the column beside it. On a phone that put the bottom
 * of one section fifty pixels above where its text really ended, and fifty
 * pixels was enough to lay the field over the last two lines of a paragraph.
 *
 * Clamped to the block, so content that overflows its own box cannot report
 * clearance that is not there, and a block with nothing in it reports none.
 */
function contentBox(block: Element): { top: number; bottom: number } {
  const own = block.getBoundingClientRect();
  const range = document.createRange();
  range.selectNodeContents(block);
  const box = range.getBoundingClientRect();
  if (!box.width && !box.height) return { top: own.top, bottom: own.bottom };
  return {
    top: Math.max(box.top, own.top),
    bottom: Math.min(box.bottom, own.bottom),
  };
}

export default function Atmosphere() {
  const root = useRef<HTMLDivElement | null>(null);
  const surface = useRef<HTMLCanvasElement | null>(null);

  /* ── The drift, and the dots ────────────────────────────────────────────── */
  useEffect(() => {
    const element = root.current;
    const canvas = surface.current;
    if (!element || !canvas) return;

    /* A reader who asked for less motion still gets the field — texture is not
       motion — but flat, still, and only in the margins. Which is why this no
       longer returns early: there is a frame to draw either way, just not one
       that repeats. */
    const stillness = window.matchMedia('(prefers-reduced-motion: reduce)');
    let still = stillness.matches;

    const context = canvas.getContext('2d');
    if (!context) return;

    let frame = 0;
    let latest = window.scrollY;
    let travel = 1;
    let seams: Seam[] = [];
    let dots: Dot[] = [];
    let sprite = makeSprite('#000');
    let ratio = 1;
    let keepOut: Frame['keepOut'] = [];
    let painted: Patch[] = [];

    /* The colour comes from CSS so it follows the theme, and the sprite is
       baked from it — so it is re-baked when the theme changes, and only
       then. */
    const reink = () => {
      const colour = getComputedStyle(canvas).color || 'rgba(0,0,0,0.2)';
      sprite = makeSprite(colour);
      /* Everything on the canvas is now the wrong colour, so the next frame
         has the whole surface to clear rather than only what it will draw. */
      painted = [{ x: 0, y: 0, w: window.innerWidth, h: window.innerHeight }];
    };

    const size = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      /* One and a half is as far as this is worth taking. The dots are a pixel
         or two across and softened at the edge; the difference between 1.5 and
         2 is not one anybody has seen, and it is nearly twice the screen to
         fill on every frame of every scroll. */
      ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      /* Resizing the backing store clears it, so there is nothing left over to
         erase and a stale patch list would only clear parts of a blank canvas. */
      painted = [];
      dots = buildField(width, height);
    };

    /* How far there is to scroll, and where in the document one block gives way
       to the next. Both measured here rather than in the frame: they are the
       only layout reads this needs, and doing them per frame would make a
       smooth scroll pay for a measurement it already knows.

       Which boundaries count: every block's top except the first — the top of
       the page is an edge, not a meeting — plus the bottom of the last one,
       where the content ends and the footer starts. That last one matters most
       on the pages that are a single block long: without it they would have no
       seam at all, and on a phone the seam band is the whole of this.

       And how much room each has. A boundary is only usable if there is empty
       ground either side of it, and the amount is not a constant — see
       contentBox. So it is measured rather than assumed, the band is centred in
       whatever gap is actually there rather than on the boundary itself, and a
       boundary with too little room is not a seam at all. That is what keeps
       the dots off the terms strip, which meets its neighbours flush.

       Kept in document coordinates so a frame can turn one into a screen
       position with a subtraction. */
    const measure = () => {
      travel = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      const blocks = Array.from(document.querySelectorAll<HTMLElement>('[data-ground]'));
      const scroll = window.scrollY;

      /* All of this is measured while the page is still settling — reveal
         animations run on entry, images land, a step of a form replaces
         itself — so a gap read to the pixel is a gap read too precisely. Each
         side gives up a little of what it measured, and what is left is ground
         the band can have without ever being wrong by a line. */
      const clear = (space: number) => Math.max(space - SAFETY, 0);

      const content = blocks.map(contentBox);

      const found: { boundary: number; above: number; below: number }[] = [];
      for (let i = 1; i < blocks.length; i += 1) {
        const edge = blocks[i].getBoundingClientRect().top;
        found.push({
          boundary: edge + scroll,
          above: clear(edge - content[i - 1].bottom),
          below: clear(content[i].top - edge),
        });
      }
      const last = blocks[blocks.length - 1];
      if (last) {
        const edge = last.getBoundingClientRect().bottom;
        found.push({
          boundary: edge + scroll,
          above: clear(edge - content[content.length - 1].bottom),
          below: last.nextElementSibling
            ? clear(contentBox(last.nextElementSibling).top - edge)
            : 0,
        });
      }

      /* Fixed furniture the field has to work around. Only the section rail
         does this today; it is read by attribute rather than by class so the
         next thing that needs it can say so itself. */
      keepOut = Array.from(document.querySelectorAll<HTMLElement>('[data-no-dots]'))
        .map((el) => el.getBoundingClientRect())
        .filter((box) => box.width > 0 && box.height > 0)
        .map((box) => ({ x0: box.left, y0: box.top, x1: box.right, y1: box.bottom }));

      seams = found
        .map(({ boundary, above, below }) => ({
          /* The middle of the gap, not the boundary: where one side has all the
             room and the other none, the band moves onto the side that has it
             instead of straddling into the text. */
          y: boundary + (below - above) / 2,
          gap: above + below,
        }))
        .filter(({ y, gap }) => Number.isFinite(y) && gap >= MIN_GAP);
    };

    const paint = () => {
      frame = 0;
      /* Driven by how far through the page you are, not by how many pixels you
         have scrolled. Pixels stop mattering on a long page — the layers would
         travel their whole range in the first screen and then sit still for the
         rest of it, which is exactly as flat as not moving at all. */
      const progress = Math.min(Math.max(latest / travel, 0), 1);
      const vh = window.innerHeight;
      const set = (name: string, value: number) =>
        element.style.setProperty(name, `${value.toFixed(1)}px`);

      set('--m-parallax-slow', progress * vh * 0.22);
      set('--m-parallax-fast', -progress * vh * 0.3);
      // The orbs travel further, and in opposite directions, so they separate
      // from each other as well as from the page.
      set('--m-orb-a-drift', progress * vh * 0.95);
      set('--m-orb-b-drift', -progress * vh * 0.8);

      /* ── The seam ───────────────────────────────────────────────────────
         Of every boundary between two blocks, the one nearest the middle of
         the screen. Nearest rather than next, so scrolling back up brings the
         same one back rather than skipping to the one above. */
      let seamY = Number.NaN;
      let seamGap = 0;
      let nearest = Infinity;
      for (const seam of seams) {
        const onScreen = seam.y - latest;
        const distance = Math.abs(onScreen - vh / 2);
        if (distance < nearest) {
          nearest = distance;
          seamY = onScreen;
          seamGap = seam.gap;
        }
      }

      let current: Frame['seam'] = null;
      if (!still && Number.isFinite(seamY)) {
        /* Full strength with the boundary at the middle of the screen, nothing
           by the time it is off either end. Smoothstepped, because a linear
           fade has a corner at each end and the corner is what you see. */
        const near = Math.min(Math.max(1 - nearest / (vh * 0.5), 0), 1);
        current = {
          y: seamY,
          half: seamGap / 2,
          strength: near * near * (3 - 2 * near),
          /* The draw. Tied to how far the boundary has risen rather than to a
             clock: it wipes in from the left on the way up the screen and back
             out on the way down, so it answers the scroll instead of playing
             at it. */
          drawn: Math.min(Math.max((vh - seamY) / (vh * 0.55), 0), 1),
        };
      }

      painted = paintDots(
        context,
        sprite,
        dots,
        {
          width: window.innerWidth,
          height: vh,
          scroll: latest,
          progress,
          time: clock,
          seam: current,
          keepOut,
          still,
        },
        painted
      );
    };

    /* ── When to draw ─────────────────────────────────────────────────────
       This used to draw only on the frames the page had moved, which made a
       still page cost nothing and also made it *look* like nothing: the field
       simply stopped. It now keeps a clock, and there are two rates.

       While the page is moving it draws every frame, as before, because the
       parallax has to keep up with the scroll. Standing still it drops to
       thirty a second, which is more than enough for something that takes four
       seconds to go round a two-pixel circle and halves what it costs to sit
       and read. The browser stops calling this altogether when the tab is not
       visible, so a page left open in the background is free.

       A reader who asked for less motion gets no loop at all: the field is
       drawn once and only redrawn when the page itself changes. */
    const FLOAT_STEP = 1000 / 30;
    let loop = 0;
    let clock = 0;
    let paintedAt = -1;
    let paintedScroll = Number.NaN;

    const tick = (now: number) => {
      loop = window.requestAnimationFrame(tick);
      const moved = latest !== paintedScroll;
      if (!moved && now - paintedAt < FLOAT_STEP) return;
      paintedAt = now;
      paintedScroll = latest;
      clock = now;
      paint();
    };

    const onScroll = () => {
      latest = window.scrollY;
      /* Under reduced motion there is no loop running to notice. */
      if (still && !frame) frame = window.requestAnimationFrame(paint);
    };

    const onResize = () => {
      size();
      measure();
      onScroll();
    };

    /* A theme change repaints the field in the new ink. Watching the attribute
       rather than being told about it keeps this the only place that has to
       know, whoever ends up flipping the switch. */
    const themed = new MutationObserver(() => {
      reink();
      onScroll();
    });
    themed.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    const scheme = window.matchMedia('(prefers-color-scheme: dark)');
    const onScheme = () => {
      reink();
      onScroll();
    };
    const run = () => {
      if (still) {
        if (loop) window.cancelAnimationFrame(loop);
        loop = 0;
        paint();
        return;
      }
      if (!loop) loop = window.requestAnimationFrame(tick);
    };

    const onStillness = () => {
      still = stillness.matches;
      run();
      onScroll();
    };

    reink();
    size();
    measure();
    paint();
    run();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    scheme.addEventListener('change', onScheme);
    stillness.addEventListener('change', onStillness);
    /* The page grows as images and revealed sections land, and a stale travel
       would leave the drift short. */
    const grew = new ResizeObserver(onResize);
    grew.observe(document.body);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      scheme.removeEventListener('change', onScheme);
      stillness.removeEventListener('change', onStillness);
      themed.disconnect();
      grew.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      if (loop) window.cancelAnimationFrame(loop);
    };
  }, []);

  return (
    <div className="m-atmosphere" aria-hidden="true" data-no-translate ref={root}>
      <div className="m-atmosphere__orb m-atmosphere__orb--a" />
      <div className="m-atmosphere__orb m-atmosphere__orb--b" />
      <div className="m-atmosphere__light" />
      <canvas className="m-atmosphere__dots" ref={surface} />
      <div className="m-atmosphere__grain" />
    </div>
  );
}
